import {
    AttendeeTableRowOption,
    BEAttendee,
    EventPrinter,
    EventProps,
    IAttendee,
    IBreakOutGroup,
    SignInGuestDialogState,
} from '@event/types/event';
import { createContext, Dispatch, SetStateAction, useContext } from 'react';
import {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
    TypedMutationTrigger,
} from '@reduxjs/toolkit/query/react';
import { PaginationReturn } from '@common/types/service';

const EventDetailAudienceContext = createContext<
    | {
          searchState: [string, React.Dispatch<React.SetStateAction<string>>];
          searchByState: [string, React.Dispatch<React.SetStateAction<string>>];
          pageState: [number, React.Dispatch<React.SetStateAction<number>>];
          rowsPerPageState: [number, React.Dispatch<React.SetStateAction<number>>];
          activeFiltersState: [
              Record<string, string[]>,
              React.Dispatch<React.SetStateAction<Record<string, string[]>>>
          ];
          anchorElState: [HTMLButtonElement | null, React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>];
          selectedAttendeesState: [number[], React.Dispatch<React.SetStateAction<number[]>>];
          getRowOptions: <T extends Partial<IAttendee> | void>(single?: boolean) => AttendeeTableRowOption<T>[];
          downloadCertificates: { fn: (subscriber_id?: number) => Promise<void>; isLoading: boolean };
          attendeesList?: { results: IAttendee[]; count: number; queryset_ids: IAttendee['id'][] };
          awaitingCount?: number;
          signedInCount?: number;
          applicationStatusPendingData?: { results: IAttendee[]; count: number; queryset_ids: IAttendee['id'][] };
          downloadList: { fn: () => Promise<void>; isLoading: boolean };
          debounceSearchInput: string;
          sortState: [
              {
                  column: keyof (BEAttendee & {
                      received_emails: 'string';
                      group__name: 'string';
                      user__food_preference: 'string';
                  });
                  direction: 'asc' | 'desc';
              },
              React.Dispatch<
                  React.SetStateAction<{
                      column: keyof (BEAttendee & {
                          received_emails: 'string';
                          group__name: 'string';
                          user__food_preference: 'string';
                      });
                      direction: 'asc' | 'desc';
                  }>
              >
          ];
          areActionsLoading: boolean;
          isBulkChunking: boolean;
          sendEmail: { fn: () => Promise<void>; isLoading: boolean };
          patchSubscribers: TypedMutationTrigger<
              IAttendee[],
              EventProps & {
                  invalidateTags?: boolean;
                  body: {
                      id: number;
                      status?: IAttendee['status'];
                      present?: IAttendee['present'];
                      group_id?: IBreakOutGroup['id'];
                      application_status?: IAttendee['application_status'];
                  }[];
              },
              BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>
          >;
          printerDialogSubscriberIds: number[] | undefined;
          setPrinterDialogSubscriberIds: Dispatch<SetStateAction<number[] | undefined>>;
          signInGuestDialogState: SignInGuestDialogState;
          setSignInGuestDialogState: Dispatch<SetStateAction<SignInGuestDialogState>>;
          printerListState: {
              printerListData?: PaginationReturn<EventPrinter>;
              isEventPrintersLoading: boolean;
              printerListSearchInput: string;
              setPrinterListSearchInput: Dispatch<SetStateAction<string>>;
              printerListRowsPerPage: number;
              setPrinterListRowsPerPage: Dispatch<SetStateAction<number>>;
              printerListPage: number;
              setPrinterListPage: Dispatch<SetStateAction<number>>;
              selectedPrinterId: number | undefined;
              setSelectedPrinterId: Dispatch<SetStateAction<number | undefined>>;
          };
      }
    | undefined
>(undefined);

const useEventDetailAudienceContext = () => {
    const context = useContext(EventDetailAudienceContext);
    if (!context) throw new Error('useEventDetailAttendeesContext must be used within a provider');
    return context;
};

export { EventDetailAudienceContext, useEventDetailAudienceContext };
